<template>
  <div
    class="spm-date-range-picker"
    :class="{ 'spm-date-range-picker--with-calendar': selectedInterval === IntervalDateEnum.CUSTOM_DATE_RANGE }"
  >
    <!-- Interval Selector Dropdown -->
    <Dropdown
      v-model="selectedInterval"
      :options="intervalOptions"
      option-label="label"
      option-value="value"
      :placeholder="t('customDatePicker.empty')"
      class="spm-date-range-picker__dropdown"
      @change="onIntervalChange"
    />

    <!-- Calendar for custom date range -->
    <Calendar
      v-if="selectedInterval === IntervalDateEnum.CUSTOM_DATE_RANGE"
      v-model="customDateRange"
      selection-mode="range"
      date-format="dd/mm/yy"
      :hide-on-range-selection="true"
      :manual-input="false"
      :min-date="computedMinDate"
      :max-date="computedMaxDate"
      class="spm-date-range-picker__calendar"
      @date-select="onCustomDateSelect"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import { IntervalDateEnum } from '@/types';

export interface DateRangeFilterValue {
  interval: IntervalDateEnum | 'empty' | string;
  customDateRange?: Date[];
}

export default defineComponent({
  name: 'SpmDateRangePicker',

  components: {
    Dropdown,
    Calendar,
  },

  props: {
    modelValue: {
      type: Object as PropType<DateRangeFilterValue | null>,
      default: null,
    },

    minYearsBack: {
      type: Number,
      default: 5,
    },

    noLimit: {
      type: Boolean,
      default: false,
    },

    customIntervals: {
      type: Array as PropType<IntervalDateEnum[]>,
      default: () => [],
    },

    showEmptyOption: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedInterval = ref<IntervalDateEnum | 'empty' | string>('empty');
    const customDateRange = ref<Date[] | null>(null);
    const maxDate = ref<Date | null>(null);

    const computedMinDate = computed(() => {
      if (props.minYearsBack <= 0) return undefined;
      const date = new Date();
      date.setFullYear(date.getFullYear() - props.minYearsBack);
      return date;
    });

    const computedMaxDate = computed(() => {
      if (props.noLimit) return undefined;
      return maxDate.value ?? new Date();
    });

    type IntervalOption = { label: string; value: IntervalDateEnum | 'empty' };

    const intervalOptions = computed((): IntervalOption[] => {
      const intervals = props.customIntervals.length > 0
        ? props.customIntervals
        : Object.values(IntervalDateEnum);

      const options: IntervalOption[] = intervals.map((interval) => ({
        label: t(`customDatePicker.${interval}`),
        value: interval,
      }));

      if (props.showEmptyOption) {
        options.unshift({
          label: t('customDatePicker.empty'),
          value: 'empty' as const,
        });
      }

      return options;
    });

    const emitValue = () => {
      if (selectedInterval.value === 'empty') {
        emit('update:modelValue', { interval: 'empty' });
        return;
      }

      if (selectedInterval.value === IntervalDateEnum.CUSTOM_DATE_RANGE) {
        if (customDateRange.value && customDateRange.value[0] && customDateRange.value[1]) {
          emit('update:modelValue', {
            interval: selectedInterval.value,
            customDateRange: customDateRange.value,
          });
        }
      } else {
        emit('update:modelValue', {
          interval: selectedInterval.value,
        });
      }
    };

    const onIntervalChange = () => {
      // Always emit when interval changes, even for CUSTOM_DATE_RANGE
      // This ensures the parent knows the interval changed
      emitValue();
    };

    const onCustomDateSelect = () => {
      if (!customDateRange.value) return;

      const startDate = customDateRange.value[0];
      const endDate = customDateRange.value[1];

      if (startDate && !endDate) {
        const oneYearLater = new Date(startDate.getTime());
        oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
        const now = new Date();
        maxDate.value = oneYearLater > now ? now : oneYearLater;
      } else if (startDate && endDate) {
        maxDate.value = new Date();
        emitValue();
      }
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        if (!newVal) {
          selectedInterval.value = 'empty';
          customDateRange.value = null;
          return;
        }

        if (newVal.interval) {
          selectedInterval.value = newVal.interval;
        }

        if (newVal.customDateRange && newVal.customDateRange.length > 0) {
          customDateRange.value = newVal.customDateRange.map((d) => (d instanceof Date ? d : new Date(d)));
        } else if (newVal.interval === IntervalDateEnum.CUSTOM_DATE_RANGE) {
          // Initialize default custom date range if not provided
          const now = new Date();
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          customDateRange.value = [thirtyDaysAgo, now];
        }
      },
      { immediate: true, deep: true },
    );

    return {
      t,
      IntervalDateEnum,
      selectedInterval,
      customDateRange,
      intervalOptions,
      computedMinDate,
      computedMaxDate,
      onIntervalChange,
      onCustomDateSelect,
    };
  },
});
</script>

<style lang="scss" scoped>
.spm-date-range-picker {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;

  &--with-calendar {
    .spm-date-range-picker__dropdown {
      :deep(.p-dropdown) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom: none;
      }
    }

    .spm-date-range-picker__calendar {
      :deep(.p-inputtext) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }

  &__dropdown {
    width: 100%;

    :deep(.p-dropdown) {
      width: 100%;
      font-size: 0.8125rem;
      border-color: #94c840;

      &:hover {
        border-color: #94c840;
      }
    }
  }

  &__calendar {
    width: 100%;

    :deep(.p-inputtext) {
      width: 100%;
      font-size: 0.8125rem;
      border-color: #94c840;
      background-color: #fafafa;
      color: #666;

      &:hover {
        border-color: #94c840;
      }
    }
  }
}
</style>
